/**
 * AlertRuleFilters.tsx
 * Feature: 125 - Alert Rule Management
 * Author: Peter Dobler
 * Date: 2026-03-25
 * Constitution Version: 1.12.0
 *
 * Purpose: Filter controls for alert rules list.
 */

import React, { useCallback } from 'react';
import { Search, X, Filter } from 'lucide-react';
import type { AlertRuleFilters as FilterType, AlertType, Severity } from '@shared/types/alertRuleManagement';
import { ALERT_TYPE_OPTIONS, SEVERITY_OPTIONS } from '@shared/types/alertRuleManagement';
import { useDebounce } from '../../../../hooks/useDebounce';

// =============================================================================
// Props
// =============================================================================

interface AlertRuleFiltersProps {
  filters: FilterType;
  onChange: (filters: FilterType) => void;
  onClear: () => void;
  hasFilters: boolean;
}

// =============================================================================
// Component
// =============================================================================

export const AlertRuleFilters: React.FC<AlertRuleFiltersProps> = ({
  filters,
  onChange,
  onClear,
  hasFilters,
}) => {
  const [searchValue, setSearchValue] = React.useState(filters.search || '');

  // Debounce search input
  const debouncedSearch = useDebounce(searchValue, 300);

  // Update filters when the debounced search changes. Compare the NORMALISED value: an empty box is `undefined` in
  // the filters, and comparing the raw '' against it was always unequal, so the effect wrote a new filters object
  // on every render until React's update cap (Spec 292 T038, audit F7).
  React.useEffect(() => {
    const next = debouncedSearch || undefined;
    if (next !== filters.search) {
      onChange({ ...filters, search: next });
    }
  }, [debouncedSearch, filters, onChange]);

  // "Clear filters" resets the parent's filters AND the local search box; otherwise the debounce wrote the old
  // search straight back (Spec 292 T038).
  const handleClearAll = useCallback(() => {
    setSearchValue('');
    onClear();
  }, [onClear]);

  const handleAlertTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value as AlertType | '';
      onChange({ ...filters, alertType: value || undefined });
    },
    [filters, onChange]
  );

  const handleSeverityChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value as Severity | '';
      onChange({ ...filters, severity: value || undefined });
    },
    [filters, onChange]
  );

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      onChange({
        ...filters,
        enabled: value === '' ? undefined : value === 'true',
      });
    },
    [filters, onChange]
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    onChange({ ...filters, search: undefined });
  }, [filters, onChange]);

  return (
    <div className="bg-[var(--color-bg-card)] rounded-lg shadow p-4">
      <div className="flex flex-col gap-4">
        {/* Filter Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-[var(--color-text-secondary)]">
            <Filter className="w-4 h-4" />
            Filters
          </div>
          {hasFilters && (
            <button
              onClick={handleClearAll}
              className="text-sm text-brand hover:text-brand-dark flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              Clear filters
            </button>
          )}
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <label htmlFor="search" className="sr-only">
              Search by name
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-secondary)]" />
              <input
                type="text"
                id="search"
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search by name..."
                className="w-full pl-10 pr-8 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-bg-card)] text-[var(--color-text-primary)] placeholder-[var(--color-text-secondary)] focus:ring-brand focus:border-brand"
              />
              {searchValue && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Alert Type Filter */}
          <div>
            <label htmlFor="alertType" className="sr-only">
              Alert Type
            </label>
            <select
              id="alertType"
              value={filters.alertType || ''}
              onChange={handleAlertTypeChange}
              className="w-full py-2 px-3 border border-[var(--color-border)] rounded-md bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:ring-brand focus:border-brand"
            >
              <option value="">All Alert Types</option>
              {ALERT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Severity Filter */}
          <div>
            <label htmlFor="severity" className="sr-only">
              Severity
            </label>
            <select
              id="severity"
              value={filters.severity || ''}
              onChange={handleSeverityChange}
              className="w-full py-2 px-3 border border-[var(--color-border)] rounded-md bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:ring-brand focus:border-brand"
            >
              <option value="">All Severities</option>
              {SEVERITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status" className="sr-only">
              Status
            </label>
            <select
              id="status"
              value={filters.enabled === undefined ? '' : String(filters.enabled)}
              onChange={handleStatusChange}
              className="w-full py-2 px-3 border border-[var(--color-border)] rounded-md bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:ring-brand focus:border-brand"
            >
              <option value="">All Statuses</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertRuleFilters;
